import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { ctmFormDefaultState } from "../../../utils/constants/CategoryTrackMapping";
import {
  GET_CATEGORY_TRACK_MAPPINGS_QUERY_KEY,
  GET_CTM_OPTIONS_QUERY_KEY,
} from "../../../utils/constants/queryKeys";
import toast from "react-hot-toast";
import CategoryTrackMappingAPIs from "../../../apiRoutes/CategoryTrackMapping";
import useAmsConfig from "../../../hooks/useAmsConfig";
import useGlobalAppAndLanguage from "../../../hooks/useGlobalAppAndLanguage";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import usePermissions from "../../../hooks/usePermissions";
import CustomModal from "../../common/CustomModal";
import CampaignsEditor from "./CampaignsEditor";

const handleCampaignPayload = ({ accounts }) =>
  accounts.map(({ meta_ad_account_id, campaigns }) => ({
    meta_ad_account_id,
    campaigns: campaigns.map(({ meta_campaign_id }) => ({ meta_campaign_id })),
  }));

/** Add or edit a track and its campaigns in one place. */
const CtmEditModal = ({ editData = null, onClose }) => {
  const { globalApp, globalLanguage } = useGlobalAppAndLanguage();
  const [initialCampaigns] = useState(() =>
    JSON.stringify(
      handleCampaignPayload({ accounts: editData?.campaign_accounts || [] })
    )
  );
  const [savedId, setSavedId] = useState(editData?.id ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [accounts, setAccounts] = useState(() =>
    (editData?.campaign_accounts || []).map((a) => ({
      ...a,
      campaigns: a.campaigns.map((c) => ({ ...c })),
    }))
  );
  const [form, setForm] = useState(() =>
    editData
      ? {
          app_name: editData.app_name ?? "",
          language: editData.language ?? "",
          category: editData.category ?? "",
          track: editData.track ?? "",
          track_number: editData.track_number ?? "",
        }
      : {
          ...ctmFormDefaultState,
          app_name: globalApp,
          language: globalLanguage,
        }
  );
  const cfg = useAmsConfig();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { isSuperAdmin } = usePermissions();
  const campaignsChanged =
    JSON.stringify(handleCampaignPayload({ accounts })) !== initialCampaigns;

  const handleSetField = ({ key, value }) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.app_name || !form.language || !form.track.trim())
      return toast.error("App, language and track are required.");
    const empty = accounts.find((a) => !a.campaigns.length);
    if (isSuperAdmin && empty)
      return toast.error(
        `Add at least one campaign for ${empty.ad_account_name}, or remove that account.`
      );
    const payload = {
      app_name: form.app_name.trim(),
      language: form.language,
      category: (form.category || "").trim(),
      track: form.track.trim(),
      track_number:
        form.track_number === "" || form.track_number == null
          ? null
          : Number(form.track_number),
    };
    setIsLoading(true);
    try {
      const saved = savedId
        ? await CategoryTrackMappingAPIs.update({
            axiosPrivate,
            id: savedId,
            payload,
          })
        : await CategoryTrackMappingAPIs.create({ axiosPrivate, payload });
      const id = savedId ?? saved?.id;
      setSavedId(id);
      if (isSuperAdmin && campaignsChanged && id)
        await CategoryTrackMappingAPIs.handleSaveCampaigns({
          axiosPrivate,
          id,
          accounts: handleCampaignPayload({ accounts }),
        });
      queryClient.invalidateQueries({
        queryKey: GET_CATEGORY_TRACK_MAPPINGS_QUERY_KEY,
      });
      queryClient.invalidateQueries({ queryKey: GET_CTM_OPTIONS_QUERY_KEY });
      toast.success(editData ? "Mapping updated." : "Mapping created.");
      onClose();
    } catch {
      queryClient.invalidateQueries({
        queryKey: GET_CATEGORY_TRACK_MAPPINGS_QUERY_KEY,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CustomModal
      open
      title={editData ? `Edit · ${editData.track}` : "Add mapping"}
      onClose={onClose}
      handleConfirmation={handleSave}
      handleConfirmationBtnText={editData ? "Save" : "Create"}
      isLoading={isLoading}
      className="md:!w-[70%] lg:!w-[50%]"
    >
      <div className="space-y-5 py-2">
        <section className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>App *</Label>
            <Select
              value={form.app_name}
              onValueChange={(v) =>
                setForm((prev) => ({ ...prev, app_name: v, language: "" }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select app" />
              </SelectTrigger>
              <SelectContent>
                {cfg.appNames.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Language *</Label>
            <Select
              value={form.language}
              onValueChange={(v) =>
                handleSetField({ key: "language", value: v })
              }
              disabled={!form.app_name}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    form.app_name ? "Select language" : "Select an app first"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {cfg.languagesFor(form.app_name).map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Input
              placeholder="e.g. Astrology"
              value={form.category}
              onChange={(e) =>
                handleSetField({ key: "category", value: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Track *</Label>
            <Input
              placeholder="e.g. astrosamadhaan"
              value={form.track}
              onChange={(e) =>
                handleSetField({ key: "track", value: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Track number</Label>
            <Input
              type="number"
              inputMode="numeric"
              placeholder="e.g. 55"
              value={form.track_number}
              onChange={(e) =>
                handleSetField({ key: "track_number", value: e.target.value })
              }
            />
          </div>
        </section>
        <section className="space-y-2 border-t border-gray-100 pt-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Campaigns</h3>
            <p className="text-xs text-gray-500">
              Each ad account rotates through its own list, top to bottom, one
              campaign per launch day. New campaigns join at the end; the order
              can&apos;t be changed.
            </p>
          </div>
          <CampaignsEditor
            accounts={accounts}
            onChange={setAccounts}
            readOnly={!isSuperAdmin}
          />
        </section>
      </div>
    </CustomModal>
  );
};

export default CtmEditModal;
